/**
 * Admin Service
 * Handles admin CRUD operations via Cloud Functions
 */

import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, doc, updateDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { db, functions } from '../config/firebase';

/**
 * Fetch all admins from Firestore
 */
export const fetchAdmins = async () => {
  try {
    const usersCollection = collection(db, 'users');
    const querySnapshot = await getDocs(usersCollection);
    
    const adminsList = [];
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      // Only get staff users (admin or operator). Master admin is excluded.
      if (data.type === 'admin' || data.type === 'operator') {
        adminsList.push({
          id: docSnap.id,
          ...data,
        });
      }
    });
    
    return adminsList;
  } catch (error) {
    console.error('Error fetching admins:', error);
    throw error;
  }
};

/**
 * Create new admin user via Cloud Function
 * This prevents the current user from being logged out
 */
export const createAdmin = async (adminData) => {
  try {
    // Check if Cloud Functions are available
    if (!functions) {
      throw new Error('Cloud Functions not initialized. Please set up Firebase Functions.');
    }

    const createAdminFunction = httpsCallable(functions, 'createAdmin');
    const result = await createAdminFunction({
      email: adminData.email,
      password: adminData.password,
      name: adminData.name,
      // Allow 'admin' or 'operator'. Defaults to 'admin' on server if missing.
      type: adminData.type || 'admin',
    });

    return result.data;
  } catch (error) {
    // If Cloud Function doesn't exist, provide helpful error
    if (error.code === 'functions/not-found') {
      throw new Error('Admin creation function not deployed. Please deploy Cloud Functions or contact your administrator.');
    }
    throw error;
  }
};

/**
 * Update admin user
 */
export const updateAdmin = async (adminId, adminData, currentUserId) => {
  try {
    const adminRef = doc(db, 'users', adminId);
    
    await updateDoc(adminRef, {
      name: adminData.name,
      updatedAt: serverTimestamp(),
      updatedBy: currentUserId,
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating admin:', error);
    throw error;
  }
};

/**
 * Delete admin user via Cloud Function
 * This properly deletes from both Firestore and Firebase Auth
 */
export const deleteAdmin = async (uid) => {
  try {
    if (!functions) {
      throw new Error('Cloud Functions not initialized.');
    }

    const deleteAdminFunction = httpsCallable(functions, 'deleteAdmin');
    const result = await deleteAdminFunction({ uid });

    return result.data;
  } catch (error) {
    if (error.code === 'functions/not-found') {
      throw new Error('Admin deletion function not deployed. Please deploy Cloud Functions.');
    }
    throw error;
  }
};

/**
 * Check if email already exists
 */
export const checkEmailExists = async (email) => {
  try {
    const usersRef = collection(db, 'users');
    const emailQuery = query(usersRef, where('email', '==', email));
    const snapshot = await getDocs(emailQuery);
    
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking email:', error);
    throw error;
  }
};
